import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HistoryIcon from "@mui/icons-material/History";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import ContractorReferralPortalApi from "../../../../infrastructure/api/contractorReferralPortal";
import JalaliDatePicker from "../../../components/shared/jalali-datepicker";
import { formatApiDate } from "../market-growth/utils/commissionValidation";
import {
  getAttributionStatusLabel,
  REFERRAL_ATTRIBUTION_STATUS_LABELS,
} from "../market-growth/constants/reportEnums";
import ContractorReferralStagesModal from "./ContractorReferralStagesModal";

const PAGE_BG = "#F6F8FB";
const PAGE_SIZE = 20;

const formatDate = (value) =>
  value ? new Date(value).toLocaleString("fa-IR") : "—";

const STATUS_OPTIONS = Object.entries(REFERRAL_ATTRIBUTION_STATUS_LABELS).map(
  ([value, label]) => ({ value, label })
);

const SummaryCards = ({ summary }) => {
  const s = summary || {};
  const items = [
    { label: "کل بازدیدها", value: s.totalVisits ?? s.TotalVisits ?? 0 },
    { label: "ثبت‌نام‌شده", value: s.registeredCount ?? s.RegisteredCount ?? 0 },
    {
      label: "دارای درخواست پروژه",
      value: s.projectCreatedCount ?? s.ProjectCreatedCount ?? 0,
    },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {items.map((item) => (
        <Grid item xs={12} sm={4} key={item.label}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">
                {item.label}
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {item.value}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

const ContractorReferralLeadsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    status: "",
    fromDate: null,
    toDate: null,
  });
  const [stagesModalAttributionId, setStagesModalAttributionId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: page + 1, pageSize: PAGE_SIZE };
      if (filters.status) params.status = filters.status;
      if (filters.fromDate) params.fromDate = formatApiDate(filters.fromDate);
      if (filters.toDate) params.toDate = formatApiDate(filters.toDate);

      const res = await ContractorReferralPortalApi.getAttributions(params);
      const { error: err, data } = ContractorReferralPortalApi.normalizeAttributions(res);
      if (err) {
        setError(err);
        setPageData(null);
        return;
      }
      setPageData(data);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
      setPageData(null);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    const items = pageData?.items ?? pageData?.Items ?? [];
    return Array.isArray(items) ? items : [];
  }, [pageData]);

  const totalCount = Number(pageData?.totalCount ?? pageData?.TotalCount ?? 0);
  const summary = pageData?.summary ?? pageData?.Summary ?? null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, background: PAGE_BG, minHeight: "100%" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            گزارش معرفی‌های من
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            وضعیت بازدید، ثبت‌نام و درخواست‌های پروژه‌ای که از لینک معرفی شما
            آمده‌اند.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            component={RouterLink}
            to="/SLN/Contractor/ContractorReferral"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            لینک معرفی
          </Button>
          <Button startIcon={<SyncIcon />} variant="outlined" onClick={load} disabled={loading}>
            بروزرسانی
          </Button>
        </Stack>
      </Stack>

      <SummaryCards summary={summary} />

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>وضعیت</InputLabel>
                <Select
                  label="وضعیت"
                  value={filters.status}
                  onChange={(e) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, status: e.target.value }));
                  }}
                >
                  <MenuItem value="">همه</MenuItem>
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <JalaliDatePicker
                label="از تاریخ"
                value={filters.fromDate}
                onChange={(value) => {
                  setPage(0);
                  setFilters((prev) => ({ ...prev, fromDate: value }));
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <JalaliDatePicker
                label="تا تاریخ"
                value={filters.toDate}
                onChange={(value) => {
                  setPage(0);
                  setFilters((prev) => ({ ...prev, toDate: value }));
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Card variant="outlined">
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>وضعیت</TableCell>
                    <TableCell>موبایل متقاضی</TableCell>
                    <TableCell>کد پروژه</TableCell>
                    <TableCell>مرحله پروژه</TableCell>
                    <TableCell>اولین بازدید</TableCell>
                    <TableCell>ثبت‌نام</TableCell>
                    <TableCell>ثبت درخواست</TableCell>
                    <TableCell align="center">تاریخچه</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          هنوز معرفی ثبت نشده است.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => {
                      const status = row.status ?? row.Status;
                      const attributionId = row.id ?? row.Id;
                      const mobile =
                        row.registeredUserMobile ??
                        row.RegisteredUserMobile ??
                        row.maskedRegisteredUserMobile ??
                        row.MaskedRegisteredUserMobile ??
                        "—";
                      return (
                        <TableRow key={attributionId} hover>
                          <TableCell>
                            <Chip
                              size="small"
                              label={getAttributionStatusLabel(status)}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{mobile}</TableCell>
                          <TableCell>{row.projectCode ?? row.ProjectCode ?? "—"}</TableCell>
                          <TableCell>
                            {row.projectStepTitle ?? row.ProjectStepTitle ?? "—"}
                          </TableCell>
                          <TableCell>{formatDate(row.firstSeenAt ?? row.FirstSeenAt)}</TableCell>
                          <TableCell>{formatDate(row.registeredAt ?? row.RegisteredAt)}</TableCell>
                          <TableCell>
                            {formatDate(row.projectCreatedAt ?? row.ProjectCreatedAt)}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="تاریخچه مراحل">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setStagesModalAttributionId(attributionId)}
                              >
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, next) => setPage(next)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} از ${count !== -1 ? count : `بیش از ${to}`}`
                }
              />
            </>
          )}
        </CardContent>
      </Card>

      <ContractorReferralStagesModal
        open={Boolean(stagesModalAttributionId)}
        attributionId={stagesModalAttributionId}
        onClose={() => setStagesModalAttributionId(null)}
      />
    </Box>
  );
};

export default ContractorReferralLeadsPage;
